import React, { useState } from 'react';
import { useInstructor } from '../context/InstructorContext';
import { Plus, Star, Copy, Download, Users, Calendar, Clock, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import BookingModal from '../components/BookingModal';
import html2canvas from 'html2canvas';

const Dashboard = () => {
    const { students, schedule, instructorBranchId, loading, createLesson } = useInstructor();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());

    const isSameDay = (d1, d2) => {
        return d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();
    };

    const handlePrevDay = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(selectedDate.getDate() - 1);
        setSelectedDate(newDate);
    };

    const handleNextDay = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(selectedDate.getDate() + 1);
        setSelectedDate(newDate);
    };

    const handleToday = () => {
        setSelectedDate(new Date());
    };

    // Filter schedule for selected date
    const dailySchedule = schedule.filter(lesson => {
        const lessonDate = new Date(lesson.start_time);
        return isSameDay(lessonDate, selectedDate);
    });

    // Filter students for the review widget
    const recentStudents = students.slice(0, 10);

    const handleBooking = async (lessonData) => {
        await createLesson(lessonData);
        alert('Lesson booked successfully!');
    };

    const copyScheduleToText = () => {
        const text = dailySchedule.length > 0
            ? dailySchedule.map(l => `${l.start_time} - ${l.lesson_type}`).join('\n')
            : "No lessons scheduled.";
        navigator.clipboard.writeText("Schedule for " + selectedDate.toLocaleDateString() + "\n" + text)
            .then(() => alert('Schedule copied!'))
            .catch(err => console.error('Failed to copy:', err));
    };

    const exportAsImage = () => {
        const element = document.getElementById('schedule-timeline');
        if (!element) return;
        html2canvas(element, { backgroundColor: '#ffffff', scale: 2 }).then(canvas => {
            const link = document.createElement('a');
            link.download = `instructor-schedule-${selectedDate.toISOString().split('T')[0]}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        });
    };

    if (loading) return (
        <div className="h-[60vh] flex items-center justify-center">
            <div className="text-slate-400 font-bold uppercase tracking-widest text-xs animate-pulse">Syncing Instructor Profile...</div>
        </div>
    );

    return (
        <div className="p-8 max-w-1600 mx-auto animate-in fade-in duration-700">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-10">
                <BookingModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleBooking}
                    students={students}
                    instructorBranchId={instructorBranchId}
                />

                {/* Left Column: Schedule (2/3 width) */}
                <div className="xl:col-span-2 space-y-8">
                    <div className="bg-white rounded-2.5rem border border-slate-100 shadow-xl shadow-slate-200/40 p-10">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                            <div>
                                <h2 className="text-2xl font-black text-slate-800 tracking-tight">Daily Schedule</h2>
                                <div className="flex items-center gap-4 mt-2">
                                    <button onClick={handlePrevDay} className="p-1 rounded-full hover:bg-slate-100 transition-colors">
                                        <ChevronLeft size={20} className="text-slate-500" />
                                    </button>
                                    <div className="text-sm font-bold text-secondary-brand uppercase tracking-widest min-w-[140px] text-center">
                                        {selectedDate.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </div>
                                    <button onClick={handleNextDay} className="p-1 rounded-full hover:bg-slate-100 transition-colors">
                                        <ChevronRight size={20} className="text-slate-500" />
                                    </button>
                                    {!isSameDay(selectedDate, new Date()) && (
                                        <button onClick={handleToday} className="text-xs font-bold text-slate-400 hover:text-secondary-brand transition-colors uppercase tracking-widest ml-2">
                                            Today
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <button onClick={copyScheduleToText} className="p-3 bg-slate-50 text-slate-600 rounded-2xl hover:bg-slate-100 transition-all border border-slate-200 shadow-sm" title="Copy Text">
                                    <Copy size={18} />
                                </button>
                                <button onClick={exportAsImage} className="p-3 bg-slate-50 text-slate-600 rounded-2xl hover:bg-slate-100 transition-all border border-slate-200 shadow-sm" title="Export Image">
                                    <Download size={18} />
                                </button>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-primary text-slate-900 px-6 py-3 rounded-2xl flex items-center gap-2.5 font-bold text-sm shadow-lg shadow-slate-200 hover:bg-yellow-400 transition-all active:scale-95"
                                >
                                    <Plus size={18} />
                                    ADD BOOKING
                                </button>
                            </div>
                        </div>

                        <div id="schedule-timeline" className="space-y-4">
                            {dailySchedule.length === 0 ? (
                                <div className="py-20 text-center">
                                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100 text-slate-200">
                                        <Calendar size={32} />
                                    </div>
                                    <h3 className="text-slate-800 font-black uppercase tracking-tight">No Lessons Scheduled</h3>
                                    <p className="text-slate-400 text-xs font-medium mt-1">New bookings will appear here in real-time.</p>
                                </div>
                            ) : (
                                dailySchedule.map((lesson, index) => {
                                    const student = students.find(s => String(s.id) === String(lesson.student_id) || String(s.id) === String(lesson.customer_id));
                                    const lessonDate = new Date(lesson.start_time);
                                    const timeStr = lessonDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

                                    return (
                                        <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                                            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-slate-900 font-bold">
                                                <Clock size={20} />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <h4 className="font-bold text-slate-800">{student?.name || 'Student'}</h4>
                                                    <span className="text-xs font-bold text-secondary-brand">{timeStr}</span>
                                                </div>
                                                <p className="text-sm text-slate-600">{lesson.lesson_type || 'Lesson'}</p>
                                                {lesson.notes && <p className="text-xs text-slate-400 mt-1">{lesson.notes}</p>}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column: Widgets */}
                <div className="space-y-8">
                    <StudentReviewWidget students={recentStudents} />

                    <div className="bg-amber-50 rounded-2.5rem border border-amber-100 p-8 relative overflow-hidden group">
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-black text-amber-900 leading-none">Weekly Summary</h3>
                                <div className="px-2 py-0.5 bg-amber-200/50 text-amber-700 text-[9px] font-black rounded-full border border-amber-300 uppercase tracking-widest">Stable</div>
                            </div>
                            <p className="text-xs font-bold text-amber-700/70 mb-6 leading-relaxed">
                                Vehicle reports and fuel logs are being harmonized with the main ledger.
                            </p>
                            <button
                                disabled
                                className="w-full bg-amber-100/50 text-amber-400 font-black py-4 rounded-2xl border-2 border-dashed border-amber-200 text-xs uppercase tracking-widest cursor-not-allowed"
                            >
                                Integrated Form Soon
                            </button>
                        </div>
                        <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-amber-400/10 blur-[60px]"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const StudentReviewWidget = ({ students }) => {
    const [rating, setRating] = useState(0);

    return (
        <div className="bg-white rounded-2.5rem border border-slate-100 shadow-xl shadow-slate-200/40 p-8">
            <h3 className="text-xl font-black text-slate-800 mb-6 tracking-tight flex items-center gap-3">
                <CheckCircle2 size={24} className="text-primary" />
                Student Review
            </h3>

            <div className="space-y-6">
                <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Select Trainee</label>
                    <select className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm font-bold text-slate-700 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 transition-all cursor-pointer">
                        {students.map(s => <option key={s.id} value={s.id}>{s.name || s.first_name}</option>)}
                        {students.length === 0 && <option>No active students found</option>}
                    </select>
                </div>

                <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Lesson Performance</label>
                    <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(star => (
                            <button
                                key={star}
                                onClick={() => setRating(star)}
                                className={`flex-1 h-12 rounded-xl flex items-center justify-center font-black transition-all ${rating >= star
                                    ? 'bg-primary text-slate-900 shadow-lg shadow-slate-200'
                                    : 'bg-slate-50 text-slate-300 border border-slate-100'
                                    }`}
                            >
                                {star}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Behavioral Flags</label>
                    <div className="grid grid-cols-2 gap-3">
                        {['Nervous', 'Reckless', 'Steady', 'Ready'].map(flag => (
                            <label key={flag} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:border-yellow-400 transition-all group">
                                <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-yellow-400" />
                                <span className="text-xs font-bold text-slate-600 group-hover:text-slate-900 uppercase tracking-tighter">{flag}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <button className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-slate-900/20 hover:bg-slate-800 transition-all active:scale-95 mt-4">
                    Submit Log
                </button>
            </div>
        </div>
    );
};

export default Dashboard;
